<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import { describeAccessDetail, resolveTeamMember } from '@/Utils/teamAccessSummary'
import { visiblePermissionRows } from '@/Utils/teamPermissionRows'

const props = defineProps({
	params: {
		type: Object,
		default: null,
	},
	member: {
		type: Object,
		default: null,
	},
	showLabel: {
		type: Boolean,
		default: false,
	},
	// Inline rendering for the card meta line (a bare span at text-tiny), vs the
	// desktop ACCESS column which renders a full-height stacked cell.
	compact: {
		type: Boolean,
		default: false,
	},
})

const store = useStore()

const rows = computed(() => visiblePermissionRows(
	store.getters['User/baseSubscription'],
	store.getters['User/addonSubscriptions'],
))
const liveCount = computed(() => (store.getters['Streams/allInOneStreams'] || []).length)
const vodCount = computed(() => (store.getters['Streams/vodStreams'] || []).length)

const data = computed(() => resolveTeamMember(props))
const activeProject = computed(() => store.getters['Projects/activeProject'])

// Someone on the roster who was never added to this workspace has no access
// here to summarise; say that instead of "No access", which would read as a
// revocation.
const notHere = computed(() => data.value.onProject === false)
const notHereLabel = computed(() => (
	activeProject.value?.name ? `Not on ${activeProject.value.name}` : 'Not on this workspace'
))

const summary = computed(() => {
	if (notHere.value) return { label: notHereLabel.value, tone: 'none', areas: [] }
	return describeAccessDetail(data.value, {
		rows: rows.value,
		liveCount: liveCount.value,
		vodCount: vodCount.value,
	})
})

const toneClass = computed(() => {
	if (summary.value.tone === 'none') return 'text-surface-6'
	if (summary.value.tone === 'full') return 'text-surface-9'
	return 'text-surface-8'
})

// A tooltip only adds value when there are concrete areas to enumerate.
const hasTooltip = computed(() => summary.value.areas.length > 0)
const tooltipLines = computed(() => summary.value.areas.map(
	(a) => (a.scope ? `${a.name} (${a.scope})` : a.name),
))
</script>

<template>
	<!-- Card meta line: short form + optional Custom hint (tooltip too). -->
	<span v-if="compact" class="inline-flex items-center gap-1 min-w-0">
		<Tooltip v-if="hasTooltip" wrapper-class="inline-flex min-w-0" interactive>
			<span class="text-tiny text-surface-7 truncate">{{ summary.label }}</span>
			<template #content>
				<p class="mb-1 font-medium">Can access</p>
				<ul class="m-0 list-none space-y-0.5 p-0">
					<li v-for="line in tooltipLines" :key="line">{{ line }}</li>
				</ul>
			</template>
		</Tooltip>
		<span v-else class="text-tiny text-surface-7 truncate">{{ summary.label }}</span>
	</span>

	<!-- Desktop ACCESS column. -->
	<div v-else class="flex flex-col justify-center gap-1 min-w-0 w-full h-full">
		<p v-if="showLabel" class="text-tiny text-surface-6 mb-0">Access</p>
		<div class="flex items-center gap-1.5 min-w-0">
			<Tooltip v-if="hasTooltip" wrapper-class="inline-flex min-w-0" interactive>
				<span class="text-sm leading-5 truncate" :class="toneClass">{{ summary.label }}</span>
				<template #content>
					<p class="mb-1 font-medium">Can access</p>
					<ul class="m-0 list-none space-y-0.5 p-0">
						<li v-for="line in tooltipLines" :key="line">{{ line }}</li>
					</ul>
				</template>
			</Tooltip>
			<span v-else class="text-sm leading-5 truncate" :class="toneClass">{{ summary.label }}</span>
		</div>
	</div>
</template>
